<template>
    <div ref="modal" :class="{'show': show, 'fade': transitionDuration > 0}" @click.self="onBackdropClick"
         class="modal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog" :class="'modal-' + size" role="document">
            <div class="modal-content">
                <div v-if="processing" class="text-center">
                    <Loader :wait-text="processingText" />
                </div>
                <div v-if="header && !processing" class="modal-header">
                    <h5 class="modal-title">
                        <slot name="title">{{ title }}</slot>
                    </h5>
                    <button v-if="showCloseButton" @click="closeModal" type="button" class="close" data-dismiss="modal"
                            aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div v-if="!processing" class="modal-body">
                    <slot/>
                </div>
                <div v-if="footer && !processing" class="modal-footer">
                    <slot name="footer"/>
                </div>
            </div>
        </div>
        <div ref="backdropEl" class="modal-backdrop" :class="{show, fade:transitionDuration > 0}"></div>
    </div>
</template>

<script>
import {computed, onMounted, onUnmounted, ref, watch} from "vue"
import {removeFromDom} from "@/lib/html"
import Loader from "@/components/Loader";

export default {
    name: "Modal",
    components: {Loader},
    emits: ['close', 'show', 'hide'],
    props: {
        show: Boolean,
        title: String,
        size: String,
        processingText: {
            type: String,
            default: 'Loading...'
        },
        backdrop: {
            type: Boolean,
            default: true
        },
        showCloseButton: {
            type: Boolean,
            default: true
        },
        footer: {
            type: Boolean,
            default: false
        },
        header: {
            type: Boolean,
            default: false
        },
        transitionDuration: {
            type: Number,
            default: 150
        },
        keyboard: {
            type: Boolean,
            default: true
        },
        processing: {
            type: Boolean,
            default: false,
        }
    },
    setup(props, {emit}) {
        const backdropEl = ref(null)
        const modal = ref(null)

        let timeout = 0;

        const closeModal = () => emit('close')

        const onKeyPress = (event) => {
            if (props.keyboard && props.show && event.key === 'Escape') {
                closeModal()
            }
        }

        const onBackdropClick = () => {
            if (props.backdrop) {
                closeModal()
            }
        }

        onMounted(() => {
            removeFromDom(backdropEl.value);
            window.addEventListener('keyup', onKeyPress);
            checkModalVisibility(props.show);
        })

        onUnmounted(() => {
            clearTimeout(timeout);
            removeFromDom(backdropEl.value);
            removeFromDom(modal.value);
            window.removeEventListener('keyup', onKeyPress);
        });

        const checkModalVisibility = value => {
            clearTimeout(timeout)

            if (value) {
                document.body.appendChild(backdropEl.value)
                document.body.appendChild(modal.value)
                modal.value.style.display = 'block'
                modal.value.scrollTop = 0

                timeout = setTimeout(() => {
                    emit('show')
                    timeout = 0
                }, props.transitionDuration)
            } else {
                timeout = setTimeout(() => {
                    modal.value.style.display = 'none'
                    removeFromDom(backdropEl.value)
                    removeFromDom(modal.value)
                    emit('hide')
                    timeout = 0
                }, props.transitionDuration)
            }
        };

        watch(() => props.show, checkModalVisibility);



        const modalSizeClass = computed(() => {
            return {[`modal-${this.size}`]: Boolean(this.size)};
        })

        return {
            backdropEl,
            modal,
            closeModal,
            onBackdropClick,
            modalSizeClass
        }
    }
}
</script>

<style scoped lang="scss">
.modal-content {
    max-height: 96vh;
    overflow: auto;
}

.loading {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: calc(100% - 63px);
    backdrop-filter: blur(8px);
    z-index: +1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
}
</style>
